import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import {
	AccidentOptions,
	FullServiceHistoryOptions,
	ReconditioningOptions,
	vehicleTypeMessages,
} from "@slfinrtl/ui-bff-routes"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useWatch } from "react-hook-form"
import { defineMessages, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { useConfig } from "../../../../hooks/useConfig/useConfig"
import type { InventoryInformationFormValues } from "../../inventoryAndIntakeForm/InventoryInformationFormValues"
import { conditionAndServiceFieldMessages } from "../common"
import { areUsedConditionInputsDisabled } from "../common/areUsedConditionInputsDisabled"
import { previousOwnersOptions } from "../common/previousOwnersOptions"
import { conditionAndServiceFieldNames } from "../conditionAndServiceFields.const"
import type { ConditionAndServiceFields } from "../types/ConditionAndServiceFields"
import { useAccidentOptions } from "./useAccidentOptions"
import { useFullServiceHistoryOptions } from "./useFullServiceHistoryOptions"
import { useLatestMaintenanceScopeOptions } from "./useLatestMaintenanceScopeOptions"
import { usePreviousOwnerUsageHistoryOptions } from "./usePreviousOwnerUsageHistoryOptions"
import { useReconditioningOptions } from "./useReconditioningOptions"

const messages = defineMessages({
	notPossible: {
		defaultMessage: "Cannot be edited for selected vehicle type: {vehicleType}",
		id: "conditionAndHistory.selectionNotPossibleForVehicleType",
	},
})

export const useConditionAndServiceFields = (
	isDraft: boolean,
	modelYear: number | undefined,
	isElectric: boolean,
): ConditionAndServiceFields => {
	const {
		accidentFree,
		batteryCapacityPercentage,
		fullServiceHistory,
		previousDamage,
		previousOwners,
		reconditioned,
		vehicleType,
	} = useWatch<InventoryInformationFormValues>()
	const { formatMessage } = useIntl()

	const config = useConfig()

	const { distance } = useDealerContext()
	const reconditioningOptions = useReconditioningOptions()
	const accidentOptions = useAccidentOptions(config.accidentFreeLabel)
	const fullServiceHistoryOptions = useFullServiceHistoryOptions()
	const latestMaintenanceScopeOptions = useLatestMaintenanceScopeOptions()
	const previousOwnerUsageHistoryOptions = usePreviousOwnerUsageHistoryOptions()
	const areUsedConditionInputsDisabledHint = useMemo(
		() =>
			vehicleType && areUsedConditionInputsDisabled({ vehicleType })
				? formatMessage(messages.notPossible, {
						vehicleType: formatMessage(vehicleTypeMessages[vehicleType]),
					})
				: undefined,
		[vehicleType, formatMessage],
	)

	const fields = useMemo((): ConditionAndServiceFields => {
		const shouldDisplayReconditionedFields = !!previousDamage
		const shouldDisplayExtendedBatteryCapacity =
			isElectric && batteryCapacityPercentage !== null && batteryCapacityPercentage !== undefined
		const shouldDisplayPreviousOwnerUsageHistory =
			config.previousOwnerUsageHistoryEnabled &&
			previousOwners !== undefined &&
			previousOwners !== null &&
			Number(previousOwners) >= 1

		return {
			accidentDescription:
				accidentFree === AccidentOptions.NOT_ACCIDENT_FREE
					? {
							disabledHint: areUsedConditionInputsDisabledHint,
							label: formatMessage(conditionAndServiceFieldMessages.accidentDescriptionLabel),
							name: conditionAndServiceFieldNames.accidentDescriptionName,
						}
					: undefined,
			accidentFree: config.accidentFreeLabel
				? {
						boldLabel: true,
						disabledHint: areUsedConditionInputsDisabledHint,
						label: formatMessage(conditionAndServiceFieldMessages.accidentFreeLabel),
						name: conditionAndServiceFieldNames.accidentFreeName,
						options: accidentOptions,
						required: true,
						withoutPreselection: true,
					}
				: undefined,
			batteryCapacityDate: shouldDisplayExtendedBatteryCapacity
				? {
						description: formatMessage(conditionAndServiceFieldMessages.batteryCapacityDateDescription),
						label: formatMessage(conditionAndServiceFieldMessages.batteryCapacityDateLabel),
						maxDate: dayjs().toISOString().substring(0, 10),
						name: conditionAndServiceFieldNames.batteryCapacityDateName,
						required: true,
					}
				: undefined,
			batteryCapacityMileage: shouldDisplayExtendedBatteryCapacity
				? {
						label: formatMessage(conditionAndServiceFieldMessages.batteryCapacityMileageLabel),
						maxLength: 6,
						name: conditionAndServiceFieldNames.batteryCapacityMileageName,
						required: true,
						type: "number",
						unit: distance,
						unitPosition: "suffix",
					}
				: undefined,
			batteryCapacityPercentage: isElectric
				? {
						description: formatMessage(conditionAndServiceFieldMessages.batteryCapacityDescription),
						disabledHint: areUsedConditionInputsDisabledHint,
						label: formatMessage(conditionAndServiceFieldMessages.batteryCapacityLabel),
						max: 100,
						min: 0,
						name: conditionAndServiceFieldNames.batteryCapacityPercentageName,
						type: "number",
						unit: "%",
						unitPosition: "suffix",
					}
				: undefined,
			carPassUrlFR: config.carPassEnabled
				? {
						debounced: isDraft ? 500 : undefined,
						label: `${formatMessage(conditionAndServiceFieldMessages.carPassUrlLabel)} (FR)`,
						name: `${conditionAndServiceFieldNames.carPassUrlName}.fr-BE`,
						placeholder: "https://public.car-pass.be/vhr/f285221f-ec22-2e8e-4f71-8a1cc81fb2e0?lang=fr",
						type: "url",
					}
				: undefined,
			carPassUrlNL: config.carPassEnabled
				? {
						debounced: isDraft ? 500 : undefined,
						label: `${formatMessage(conditionAndServiceFieldMessages.carPassUrlLabel)} (NL)`,
						name: `${conditionAndServiceFieldNames.carPassUrlName}.nl-BE`,
						placeholder: "https://public.car-pass.be/vhr/f285221f-ec22-2e8e-4f71-8a1cc81fb2e0?lang=nl",
						type: "url",
					}
				: undefined,
			firstRegistrationDate: config.firstRegistrationDateAvailable
				? {
						disabledHint: areUsedConditionInputsDisabledHint,
						label: formatMessage(conditionAndServiceFieldMessages.firstRegistrationDateLabel),
						maxDate: new Date().toISOString().substring(0, 10),
						minDate: modelYear ? `${modelYear - 2}-01-01` : undefined,
						name: conditionAndServiceFieldNames.firstRegistrationDateName,
						required: !areUsedConditionInputsDisabledHint,
					}
				: undefined,
			formerRentalCar: {
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.formerRentalCarLabel),
				name: conditionAndServiceFieldNames.formerRentalCarName,
			},
			fullServiceHistory: {
				boldLabel: true,
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.fullServiceHistoryLabel),
				name: conditionAndServiceFieldNames.fullServiceHistoryName,
				options: fullServiceHistoryOptions,
				withoutPreselection: true,
			},
			fullServiceHistoryInPorscheCenter:
				fullServiceHistory === FullServiceHistoryOptions.COMPLETE
					? {
							disabledHint: areUsedConditionInputsDisabledHint,
							label: formatMessage(
								conditionAndServiceFieldMessages.fullServiceHistoryInPorscheCenterLabel,
							),
							name: conditionAndServiceFieldNames.fullServiceHistoryInPorscheCenterName,
						}
					: undefined,
			knownAccidents: !config.accidentFreeLabel
				? {
						boldLabel: true,
						disabledHint: areUsedConditionInputsDisabledHint,
						label: formatMessage(conditionAndServiceFieldMessages.knownAccidentsLabel),
						name: conditionAndServiceFieldNames.accidentFreeName,
						options: accidentOptions,
						required: true,
						withoutPreselection: true,
					}
				: undefined,
			latestMaintenanceDate: {
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(commonMessages.date),
				name: conditionAndServiceFieldNames.latestMaintenanceDateName,
			},
			latestMaintenanceMileage: {
				debounced: isDraft ? 500 : undefined,
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.latestMaintenanceMileageLabel),
				maxLength: 6,
				name: conditionAndServiceFieldNames.latestMaintenanceMileageName,
				type: "number",
				unit: distance,
				unitPosition: "suffix",
			},
			latestMaintenanceScope: {
				description: formatMessage(conditionAndServiceFieldMessages.latestMaintenanceScopeDescription),
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.latestMaintenanceScopeLabel),
				name: conditionAndServiceFieldNames.latestMaintenanceScopeName,
				options: latestMaintenanceScopeOptions,
			},
			latestMaintenanceWorkshop: {
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.latestMaintenanceWorkshopLabel),
				name: conditionAndServiceFieldNames.latestMaintenanceWorkshopName,
				type: "text",
			},
			mileage: {
				debounced: isDraft ? 500 : undefined,
				label: formatMessage(conditionAndServiceFieldMessages.mileageLabel),
				maxLength: 6,
				name: conditionAndServiceFieldNames.mileageName,
				required: true,
				type: "number",
				unit: distance,
				unitPosition: "suffix",
			},
			nextGeneralInspectionDate: {
				boldLabel: true,
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.nextGeneralInspectionDateLabel),
				name: conditionAndServiceFieldNames.nextGeneralInspectionDateName,
			},
			nonSmoker: {
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.nonSmokerLabel),
				name: conditionAndServiceFieldNames.nonSmokerName,
			},
			previousDamage: {
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.previousDamageLabel),
				name: conditionAndServiceFieldNames.previousDamageName,
			},
			previousDamageAmount: shouldDisplayReconditionedFields
				? {
						debounced: isDraft ? 500 : undefined,
						label: formatMessage(conditionAndServiceFieldMessages.previousDamageAmountLabel),
						name: conditionAndServiceFieldNames.previousDamageAmountName,
						type: "currency",
					}
				: undefined,
			previousDamageDescription: shouldDisplayReconditionedFields
				? {
						debounced: isDraft ? 500 : undefined,
						label: formatMessage(conditionAndServiceFieldMessages.previousDamageDescriptionLabel),
						name: conditionAndServiceFieldNames.previousDamageDescriptionName,
					}
				: undefined,
			previousOwners: {
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.previousOwnersLabel),
				name: conditionAndServiceFieldNames.previousOwnersName,
				options: previousOwnersOptions,
				required: config.previousOwnersRequired,
			},
			previousOwnerUsageHistory: shouldDisplayPreviousOwnerUsageHistory
				? {
						disabledHint: areUsedConditionInputsDisabledHint,
						label: formatMessage(conditionAndServiceFieldMessages.previousOwnerUsageHistoryLabel),
						name: conditionAndServiceFieldNames.previousOwnerUsageHistoryName,
						options: previousOwnerUsageHistoryOptions,
						required: true,
					}
				: undefined,
			reconditioned: {
				boldLabel: true,
				label: formatMessage(conditionAndServiceFieldMessages.reconditioningLabel),
				name: conditionAndServiceFieldNames.reconditionedName,
				options: reconditioningOptions,
			},
			reconditioningAmount:
				reconditioned === ReconditioningOptions.RECONDITIONED
					? {
							debounced: isDraft ? 500 : undefined,
							label: formatMessage(conditionAndServiceFieldMessages.actualCostsLabel),
							name: conditionAndServiceFieldNames.reconditioningAmountName,
							type: "currency",
						}
					: undefined,
			reconditioningApproximateAmount:
				reconditioned === ReconditioningOptions.NOT_RECONDITIONED
					? {
							debounced: isDraft ? 500 : undefined,
							label: formatMessage(conditionAndServiceFieldMessages.approximatedCostsLabel),
							name: conditionAndServiceFieldNames.reconditioningApproximateAmountName,
							type: "currency",
						}
					: undefined,
			renewGeneralInspectionOnSale: {
				disabledHint: areUsedConditionInputsDisabledHint,
				label: formatMessage(conditionAndServiceFieldMessages.newOnSaleLabel),
				name: conditionAndServiceFieldNames.renewGeneralInspectionOnSaleName,
			},
		}
	}, [
		previousDamage,
		isElectric,
		formatMessage,
		accidentFree,
		areUsedConditionInputsDisabledHint,
		config.accidentFreeLabel,
		config.carPassEnabled,
		config.firstRegistrationDateAvailable,
		batteryCapacityPercentage,
		config.previousOwnersRequired,
		config.previousOwnerUsageHistoryEnabled,
		accidentOptions,
		modelYear,
		fullServiceHistoryOptions,
		fullServiceHistory,
		isDraft,
		distance,
		latestMaintenanceScopeOptions,
		previousOwners,
		previousOwnerUsageHistoryOptions,
		reconditioningOptions,
		reconditioned,
	])

	return fields
}
